import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";

// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore, useActions } from "../../store";
import { actions as LoaderActions } from "../../store/loadingScreen/actions";
import { actions as CartActions } from "../../store/cartDetails/actions";
import { actions as OrderActions } from "../../store/order/actions";
import { LoadingStep } from "../../store/paymentLoadingStatus/state";
import { actions as PaymentLoadingStatusActions } from "../../store/paymentLoadingStatus/actions";
import { clientFetch } from "@flights/client-fetch";

import {
  UIAlternativeFaresReason,
  UIFinalizeOrderResponse,
  UIPrice,
  UIPriceBreakdown,
  UIPriceChangeResolution,
  UIPriceChangeResolutionResponse,
  UIPriceChangeResponse,
  UIRedirect3DS
} from "@flights/types";

import useGlobalContext from "../../hooks/useGlobalContext";
import useEventTracking, { useTransactionTracking } from "../../hooks/useEventTracking";
import { usePaymentComponent } from "../../hooks/usePaymentComponent";

import useFormatPrice from "../../hooks/useFormatPrice";

import useCreateUrl from "../../hooks/useCreateUrl";
import useContextName from "../../hooks/useContextName";
import { assert } from "../../utils/assert";
import { useCheckoutProcessor } from "../../hooks/useCheckoutProcessor";
import useSearchURL from "../../hooks/useSearchURL";
import { MpStoreState } from "@bookingcom/mp-flights";
import useEvents from "../../hooks/useEvents";
import useClientMetrics from "../../hooks/useClientMetrics";
import {
  createTimer,
  flightsLocalStore,
  flightsSessionStore,
  pricingUtils,
  getMarketingTrackingVariable
} from "@flights/web-api-utils-universal";
import useOfferCartOrderData from "../../hooks/useOfferCartOrderData";
import { useTrackAgencyPaymentErrors } from "../../utils/experiments/order/flights_agency_payment_errors_web";
import useSendTravelProductBookingProcessC360Event from "../../hooks/c360/useSendTravelProductBookingProcessC360Event";
import flights_payment_component_pay_now_validation_web, {
  useTrackPaymentComponentValidation
} from "../../utils/experiments/order/flights_payment_component_pay_now_validation_web";
import flights_prevent_infinite_loader_gpay_and_paypal_web, {
  useTrackInfiniteLoaderBlock
} from "../../utils/experiments/order/flights_prevent_infinite_loader_gpay_and_paypal_web";
import useCheckoutNavigation from "../../hooks/checkout/useCheckoutNavigation";
import useCheckPersistedSelectedAncillaries from "../../hooks/useCheckPersistedSelectedAncillaries";
import useClientCachedURL from "../../hooks/useClientCachedURL";
import { ALTERNATIVE_FARES_RESULTS } from "../../constants";
import { useI18n, t } from "@bookingcom/lingojs-react";

import {
  getHadPhoneNumberError,
  getHadEmailError,
  getAnyValidationErrorTracked
} from "../../store/cartValidationError/selectors";

import { useTrackPaxValidationGoals } from "../../utils/experiments/order/flights_traveller_information_validation_highlight";
import { useSelector } from "react-redux";

import { actions as CartValidationErrorActions } from "../../store/cartValidationError/actions";
import flights_split_bounds_us, {
  splitBoundsTrackingStorage
} from "../../utils/experiments/funnel/flights_split_bounds_us";
// eslint-disable-next-line  @typescript-eslint/no-unused-vars
import flights_pc_loading_steps_desktop, {
  useTrackPaymentComponentLoadingSteps
} from "../../utils/experiments/order/flights_pc_loading_steps_desktop";
import { goal } from "@flights/et-universal";
import useProfiler from "../../hooks/useProfiler";
import useFlightsWebSouthwest from "../../utils/experiments/funnel/flights_web_southwest";
import flights_alternative_fares_for_price_change_and_sold_out_errors_web, {
  useTrackMainAlternativeFaresStage,
  useTrackSelectedAlternativeFare
} from "../../utils/experiments/order/flights_alternative_fares_for_price_change_and_sold_out_errors_web";
import flights_web_aa_lack_of_choice from "../../utils/experiments/funnel/flights_web_aa_lack_of_choice";
import { isSoldOutFareError } from "../../utils/error-utils";
import flights_web_allow_skip_extras from "../../utils/experiments/funnel/flights_web_allow_skip_extras";

export type CheckoutControllerData = ReturnType<typeof useCheckoutPayment>;

export const PAYMENT_CAPTURED_PARAM = "paymentCaptured";
export const REASON_PARAM = "reason";

interface Props {
  children: (data: CheckoutControllerData) => React.ReactElement;
}

const useIsCTALoading = (
  isPriceChangeWithoutCharge: boolean,
  pcStatus: string,
  priceChangeVerificationState: boolean
) => {
  let loading = false;

  // If we are on old price change flow, where we check price during /finalize, then we need to reenable the CTA when we get the "isPriceChangeWithoutCharge" state.
  if (!isPriceChangeWithoutCharge) {
    loading = pcStatus === "processing";
  }
  // Alternatively, for the new price change flow, we just check the verification state.
  return loading || priceChangeVerificationState;
};

const useCheckoutPayment = function () {
  const history = useHistory();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { jsError } = useEvents();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const loaderActions = useActions(LoaderActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const cartActions = useActions(CartActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const orderActions = useActions(OrderActions);

  const { debugAllExtraProducts, requestId } = useGlobalContext();
  const trackV2 = useEventTracking("checkout3", requestId);

  const [mpState, setMpState] = useState<MpStoreState>({ isLoading: false, multiProducts: [] });

  const searchURLs = useSearchURL();
  const checkout = useCheckoutProcessor();
  const { trackClientMetric } = useClientMetrics();

  const trackTransaction = useTransactionTracking();
  const { createUrl } = useCreateUrl();
  const [retrying, setRetrying] = useState(false);
  const contextName = useContextName();
  const [is3DSPaymentForUS, setIs3DSPaymentForUS] = useState(false);
  const [isPriceChangeWithoutCharge, setPriceChangeWithoutCharge] = useState(false);

  const [priceChangeVerificationState, setPriceChangeVerificationState] = useState(false);
  const [priceChangeProcessingState, setPriceChangeProcessingState] = useState(false);
  const [priceChangeState, setPriceChangeState] = useState<UIPriceChangeResponse | undefined>();
  const [showPriceChangeModal, setShowPriceChangeModal] = useState(false);
  const [priceChangeError, setPriceChangeError] = useState<string>();

  const [isValidating, setIsValidating] = useState(false);
  const [validationErrorCount, setValidationErrorCount] = useState(0);

  const { trackConfirmationError } = useTrackAgencyPaymentErrors();

  const { grumble } = useEvents();

  const loadingStepsEnabled = useRef(false);

  const { flight } = store.flightDetails;
  const { order } = store.order;
  const { cartDetails } = store.cartDetails;
  const searchId = store.searchResults?.searchId;
  const { priceToValue } = useFormatPrice();
  const sendTravelProductBookingProcessC360Event = useSendTravelProductBookingProcessC360Event();
  const {
    trackClient,
    trackClickPayWithPCValidationError,
    trackPayAfterPaymentComponentError,
    trackPCValidationAttempts,
    trackVerifyPriceChangeCalls,
    trackHadPCValidationError
  } = useTrackPaymentComponentValidation();
  const { trackPaymentErrorExists, trackPcLoadingClientAndSupplierType, trackPriceChangeErrorExists } =
    useTrackPaymentComponentLoadingSteps();
  const { trackDesktopMdot } = useTrackInfiniteLoaderBlock();
  useCheckoutNavigation();
  const trackMainAlternativeFaresStage = useTrackMainAlternativeFaresStage();

  const i18n = useI18n();
  const travelerHadPhoneValidationError = useSelector(getHadPhoneNumberError);
  const travelerHadEmailValidationError = useSelector(getHadEmailError);
  const anyValidationErrorGoalAlreadyTracked = useSelector(getAnyValidationErrorTracked);

  const { trackAnyValidationErrorAndClickPay } = useTrackPaxValidationGoals();

  const flightsWebSouthwest = useFlightsWebSouthwest();

  if (!order) {
    throw new Error("Missing order data");
  }

  const dispatch = useDispatch();
  const PRICE_CHANGE_WITHOUT_CHARGE_ERROR = "CART_MANAGEMENT_FINALIZEORDER_CART_PRICE_CHANGED_WITHOUT_CHARGE";

  const {
    orderToken,
    orderId,
    otaCartReference,
    otaCartToken,
    airOrder,
    totalPrice,
    passengers,
    ancillaries,
    includedProductsBySegment
  } = order;

  useEffect(() => {
    // Destroying store cart to make sure no components would use it on payment step (`order` should be used instead)
    cartActions.reset();
    flightsSessionStore.unset("AGENCY_PAYMENT_ERROR");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (store.order?.orderCreated?.orderToken && priceChangeProcessingState) {
      const flightToken = store.flightDetails.flight?.token;
      const orderToken = store.order?.orderCreated?.orderToken;
      const url = createUrl(`/checkout/payment/${flightToken}/${orderToken}?priceChange=true`);
      window.location.replace(url);
    }
  }, [
    history,
    store.flightDetails,
    store.order,
    store.cartDetails,
    priceChangeProcessingState,
    orderActions,
    loaderActions,
    createUrl
  ]);

  const { getLastSuccessfulSearchUrl } = useClientCachedURL();
  const lastSearchUrl = getLastSuccessfulSearchUrl();

  const goToAlternativeFares = useCallback(
    (options?: { paymentCaptured: boolean; reason: UIAlternativeFaresReason }) => {
      const lastSearchUrlParams = new URLSearchParams();

      if (lastSearchUrl) {
        const [, queryString] = lastSearchUrl.split("?");
        if (queryString) {
          const searchUrlParams = new URLSearchParams(queryString);
          searchUrlParams.forEach((value, key) => {
            lastSearchUrlParams.set(key, value);
          });
        }
      }

      if (options) {
        if (options.paymentCaptured) {
          lastSearchUrlParams.set(PAYMENT_CAPTURED_PARAM, "1");
        }

        if (options.reason) {
          lastSearchUrlParams.set(REASON_PARAM, options.reason);
        }
      }

      const path = `/checkout/alternativeFares/${flight?.token}?${lastSearchUrlParams}`;
      history.replace(createUrl(path));
    },
    [flight?.token, history, createUrl, lastSearchUrl]
  );

  const goToConfirmation = useCallback(() => {
    const params = new URLSearchParams();
    const path = `/booking/confirmation/${orderToken}?${params}`;
    history.replace(createUrl(path));
  }, [history, orderToken, createUrl]);

  const goToRyanairConfirmation = useCallback(
    (iframeUrl: string) => {
      if (!orderToken) return;
      const path = createUrl(generatePath("/checkout/ryanair-confirmation/:token", { token: orderToken }));
      const param = `url=${encodeURIComponent(iframeUrl)}`;
      const url = path.includes("?") ? `${path}&${param}` : `${path}?${param}`;
      history.replace(url);
    },
    [history, orderToken, createUrl]
  );

  const goToPriceChanged = useCallback(
    (code: string) => {
      const params = new URLSearchParams();
      if (flight) {
        params.set("flightToken", flight.token);
      }
      if (otaCartReference) {
        params.set("reference", otaCartReference);
      }
      if (otaCartToken) {
        params.set("cartToken", otaCartToken);
      }

      /**
       * There are two cases here, if we haven't deducted the amount from the user yet we will be showing the price change modal on the payment screen
       * itself which is the case without charge. Else the user lands into the old price change flow on a new screen.
       */

      if (code === PRICE_CHANGE_WITHOUT_CHARGE_ERROR) {
        setPriceChangeWithoutCharge(true);
      } else {
        history.replace(createUrl(`/checkout/pricechange/${orderToken}?${params}`));
        window.location.reload();
      }
    },
    [flight, otaCartReference, otaCartToken, history, createUrl, orderToken]
  );

  const postToUrl = (url: string, params: { [k: string]: string }) => {
    const form = document.createElement("form");
    form.action = url;
    form.method = "POST";
    for (const i in params) {
      if (params.hasOwnProperty(i)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = i;
        input.value = params[i];
        form.appendChild(input);
      }
    }
    document.body.appendChild(form);
    form.submit();
  };

  const handleSupplier3DsRedirect = useCallback((redirect3dsData: UIRedirect3DS) => {
    const { redirectUrl, bodyParams } = redirect3dsData;
    setIs3DSPaymentForUS(true);
    postToUrl(redirectUrl, bodyParams);
  }, []);

  const offerCartOrderData = useOfferCartOrderData();

  const parentOfferToken = useMemo(() => {
    const { parentOfferToken, offer } = offerCartOrderData.get(orderToken);
    if (!offer || typeof parentOfferToken !== "string") return undefined;
    if (!parentOfferToken || typeof parentOfferToken !== "string") undefined;
    if (parentOfferToken === offer) return undefined;
    return parentOfferToken;
  }, [offerCartOrderData, orderToken]);

  const finalize = useCallback(() => {
    const loadingStepsEnabledInSession = flightsSessionStore.get("PC_LOADING_STEPS_ENABLED");
    if (loadingStepsEnabled.current || loadingStepsEnabledInSession) {
      dispatch(PaymentLoadingStatusActions.setLoadingStep(LoadingStep.FINALIZING_ORDER));
    }
    const priceChangeErrorCodes = [
      "CART_MANAGEMENT_FINALIZEORDER_CART_PRICE_CHANGED",
      PRICE_CHANGE_WITHOUT_CHARGE_ERROR
    ];

    const alternativeFaresErrors = [
      "CART_RESERVATION_AIR_PRODUCT_NOT_AVAILABLE",
      "CART_MANAGEMENT_FINALIZEORDER_CART_PRICE_CHANGED",
      "CART_MANAGEMENT_FINALIZEORDER_CART_PRICE_CHANGED_WITHOUT_CHARGE"
    ] as const;

    let orderFinalizeError: any;
    let redirect3dsData: UIRedirect3DS | undefined;
    let allowAgencyPaymentRetry: boolean;
    let externalVerification: UIFinalizeOrderResponse["externalVerification"];
    loaderActions.show();

    //landingUrl3ds is required for US PointofSale where the payment is handled on etg side (US)
    const data = {
      landingUrl3ds: location.href,
      parentOfferToken,
      searchId,
      gclid: getMarketingTrackingVariable("gclid") || null,
      gbraid: getMarketingTrackingVariable("gbraid") || null
    };

    grumble("Calling finalize order", { orderId });
    return clientFetch<UIFinalizeOrderResponse>(`/api/order/finalize/${orderToken}`, {
      method: "post",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName,
        "X-Booking-Experiment-Prepare-Clientside-Payload": "1"
      },
      body: JSON.stringify(data)
    })
      .then((data) => {
        const flightDetails = data.airOrder.flightSegments[0];
        const itinerary = `${flightDetails.departureAirport.code}-${flightDetails.arrivalAirport.code}`;
        const { total, tax } = data.totalPrice;
        redirect3dsData = data.redirect3dsData;
        externalVerification = data.externalVerification;
        orderActions.fetchSuccess(data);
        allowAgencyPaymentRetry = false;

        const agencyPaymentError = data.agencyPaymentStatus;
        if (!!agencyPaymentError && data.orderStatus !== "ERROR") {
          const newPaymentId = data.airOrder?.paymentId;

          if (!newPaymentId) {
            throw new Error("No new payment ID for agency payment retry");
          }

          let errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_our_error"));

          switch (agencyPaymentError) {
            case "ORDER_FINALIZE_PAYMENT_INSUFFICIENT_FUND":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_insufficient_funds"));
              break;
            case "ORDER_FINALIZE_PAYMENT_PROCESSING_ERROR":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_generic_try_another"));
              break;
            case "ORDER_FINALIZE_PAYMENT_INCORRECT_CARD_DETAILS":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_generic_check_details"));
              break;
            case "ORDER_FINALIZE_PAYMENT_CARD_ISSUE":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_issue_with_card"));
              break;
            case "ORDER_FINALIZE_PAYMENT_TRANSACTION_REFUSED":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_bank_refused"));
              break;
            case "ORDER_FINALIZE_PAYMENT_CARD_PROCESSING_FAILED":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_issue_with_card"));
              break;
            case "ORDER_FINALIZE_PAYMENT_SOMETHING_WENT_WRONG":
              errorMessage = i18n.trans(t("flights_checkandpay_failed_retry_our_error"));
              break;
          }

          // Set agency payment error in session storage so it can be displayed when the PC reloads after changing the payment ID bellow
          flightsSessionStore.set("AGENCY_PAYMENT_ERROR", errorMessage);
          allowAgencyPaymentRetry = true;
          pcChangePaymentId(newPaymentId);
        }

        if (data.orderStatus == "CONFIRMED" || data.orderStatus == "PENDING") {
          void trackTransaction({
            id: data.orderId,
            obfuscatedOtaOrderReference: data.obfuscatedOtaOrderReference,
            itinerary,
            revenue: priceToValue(total),
            tax: priceToValue(tax),
            currency: total.currencyCode,
            partner: data.salesInfo?.partner,
            maskedUserEmail: data.booker.maskedCustomerEmail,
            maskedPhoneNumber: data.booker.maskedCustomerPhoneNumber
          });
        }

        if (data.orderStatus == "ERROR") {
          trackConfirmationError();
        }
      })
      .catch((error) => {
        orderFinalizeError = error;
      })
      .finally(() => {
        sendTravelProductBookingProcessC360Event();
        /**
         * There can be 3 cases here:
         * 1. For US POS in case there is a 3Ds payment we need to redirect to supplier3Ds page
         * 2. Order Finalized can give price change error and we need to redirect user to price change screen
         * 3. Order can get confirmed and user needs to be redirected to confirmation screen. This can happen at different instances:
         *    3.1 In case of US POS Finalized is called for the second time after supplier3Ds is successful and user is redirected to confirmation
         *    3.2 In case of usual 3Ds payment Finalized is called after 3Ds is successful and user is redirected to confirmation
         *    3.3 Finalized is called after a non3Ds payment and user is redirected to confirmation
         */
        if (redirect3dsData && redirect3dsData.redirectUrl) {
          grumble("Redirecting to 3DS", { redirect3dsData, orderId });
          handleSupplier3DsRedirect(redirect3dsData);
        } else if (alternativeFaresErrors.includes(orderFinalizeError?.code)) {
          flights_alternative_fares_for_price_change_and_sold_out_errors_web.stages.finalize_price_change_sold_out();

          if (trackMainAlternativeFaresStage()) {
            const reason =
              orderFinalizeError?.code === "CART_RESERVATION_AIR_PRODUCT_NOT_AVAILABLE"
                ? "FARE_SOLD_OUT"
                : "PRICE_CHANGED";
            loaderActions.hide();
            goToAlternativeFares({ paymentCaptured: true, reason });
          }
        } else if (priceChangeErrorCodes.includes(orderFinalizeError?.code)) {
          loaderActions.hide();
          goToPriceChanged(orderFinalizeError?.code);
        } else {
          if (orderFinalizeError) {
            jsError(orderFinalizeError);
          }
          loaderActions.hide();
          /**
           * In case of redirect3Ds flow from the supplier we should not redirect to confirmation page, if we have redirectUrl for 3dsecure.
           * In this case the form submit needs to happen to take user to the 3ds page.
           * Only when finalized is called for the second time post redirection back from 3dSecure we should go to confirmation.
           */
          if (externalVerification?.name === "ryanair") {
            goToRyanairConfirmation(externalVerification.url);
          } else {
            !is3DSPaymentForUS && !allowAgencyPaymentRetry && goToConfirmation();
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaderActions,
    searchId,
    grumble,
    orderId,
    orderToken,
    contextName,
    orderActions,
    parentOfferToken,
    trackTransaction,
    priceToValue,
    handleSupplier3DsRedirect,
    goToPriceChanged,
    is3DSPaymentForUS,
    goToConfirmation,
    jsError,
    sendTravelProductBookingProcessC360Event,
    trackConfirmationError,
    t
  ]);

  const handlePaymentFailed = useCallback(() => {
    dispatch(LoaderActions.hide());
    trackPaymentErrorExists();
    return Promise.resolve();
  }, [trackPaymentErrorExists, dispatch]);

  const paymentId = airOrder && airOrder.paymentId;
  const paymentComponentProductType = order?.supplierInfo?.paymentComponentProductType;
  const {
    pcContainerRef,
    pcProcessPayment,
    pcStatus,
    pcIsLoading,
    pcIsLoaded,
    pcTriggerValidation,
    pcInputValid,
    pcEnableLoader,
    pcPaymentMethod,
    pcChangePaymentId,
    pcShowError
  } = usePaymentComponent({
    paymentId,
    paymentComponentProductType,
    successfulCallback: finalize,
    pendingCallback: finalize,
    failedCallback: handlePaymentFailed,
    orderId,
    type: "order"
  });

  const [isInfiniteLoaderVariant, setIsPreventInfiniteLoaderVariant] = useState(false);
  const baseIsCTALoading = useIsCTALoading(isPriceChangeWithoutCharge, pcStatus, priceChangeVerificationState);

  const isCTALoading = useMemo(() => {
    return isInfiniteLoaderVariant ? baseIsCTALoading && pcEnableLoader : baseIsCTALoading;
  }, [pcEnableLoader, isInfiniteLoaderVariant, baseIsCTALoading]);

  const redirectToSearch = useCallback(() => {
    location.href = `${location.origin}${searchURLs?.searchresults}`;
  }, [searchURLs]);

  const redirectToFlightDetails = useCallback(
    (token: string) => {
      const searchResultsUrl = searchURLs?.searchresults;

      if (!searchResultsUrl) {
        throw new Error("Alternative Fares Redirect: Search Results URL was not found");
      }

      const { origin, pathname, search } = new URL(searchResultsUrl);
      const newUrl = new URL(`${pathname}${token}/${search}`, origin);

      location.href = newUrl.href;
    },
    [searchURLs]
  );

  /** Verify with ETG if the price changed before capturing the payment. */
  const verifyPriceChange = useCallback(() => {
    trackVerifyPriceChangeCalls();

    if (loadingStepsEnabled.current) {
      dispatch(PaymentLoadingStatusActions.setLoadingStep(LoadingStep.VERIFYING_PRICE_CHANGE));
      loaderActions.show();
    }

    const addOnOrderId = store.order.addOnOrder?.addOnOrderId;
    setPriceChangeVerificationState(true);

    let verifyPriceChangeUrl = `/api/order/verifyPriceChange/${orderToken}`;
    if (addOnOrderId) {
      verifyPriceChangeUrl += `?addOnOrderId=${addOnOrderId}`;
    }

    const timer = createTimer();
    clientFetch<UIPriceChangeResponse>(verifyPriceChangeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName
      }
    })
      .then((priceChangeResponse) => {
        setPriceChangeVerificationState(false);
        const time = timer();
        trackClientMetric("verify_price_change_latency", time);
        const orderId = order?.orderId;
        if (priceChangeResponse.status === "AWAITING_PAYMENT") {
          if (loadingStepsEnabled.current) {
            dispatch(PaymentLoadingStatusActions.setLoadingStep(LoadingStep.VERIFYING_PAYMENT));
          }
          pcProcessPayment(loadingStepsEnabled.current);
        } else if (priceChangeResponse.status === "PRICE_CHANGE") {
          loaderActions.hide();
          setPriceChangeState(priceChangeResponse);
          setShowPriceChangeModal(true);
        } else if (priceChangeResponse.status === "CART_RESERVATION_AIR_PRODUCT_NOT_AVAILABLE") {
          grumble("User taken into alt fares experience", { orderId });
          // Clear alternative fares cache before going to alternative fares page and
          // fetching fresh alternative fares for the current sold out fare
          flightsLocalStore.unset(ALTERNATIVE_FARES_RESULTS);
          loaderActions.hide();
          goToAlternativeFares();
        } else if (priceChangeResponse.status === "FORCE_ALTERNATIVE_FARES_FLOW") {
          grumble("Order forced into Alt fares experience", { orderId });
          flightsLocalStore.unset(ALTERNATIVE_FARES_RESULTS);
          loaderActions.hide();
          goToAlternativeFares();
        } else {
          setPriceChangeState(undefined);
          setPriceChangeError("Order is not available during price change verification.");
          trackPriceChangeErrorExists();
        }
      })
      .catch((error) => {
        const errorCode = error?.code || error?.message;
        const orderId = order?.orderId;

        // We could get fare sold out results as an error, we should still go to alternative fares
        if (isSoldOutFareError(errorCode)) {
          grumble("User taken into alt fares experience from sold out errors", { orderId });
          flightsLocalStore.unset(ALTERNATIVE_FARES_RESULTS);
          loaderActions.hide();
          goToAlternativeFares();
          return;
        }

        setPriceChangeError("Error during order price change verification");
        setPriceChangeVerificationState(false);
        trackPriceChangeErrorExists();
      });
  }, [
    store.order.addOnOrder?.addOnOrderId,
    order?.orderId,
    orderToken,
    contextName,
    grumble,
    trackClientMetric,
    pcProcessPayment,
    goToAlternativeFares,
    trackVerifyPriceChangeCalls,
    trackPriceChangeErrorExists,
    dispatch,
    loaderActions
  ]);

  const createUpdatedPriceOrder = useCallback(async () => {
    setPriceChangeProcessingState(true);

    await checkout.createOrder(cartDetails?.cart, {
      priceChangeOrderId: order.orderId,
      basketReference: cartDetails?.basketReference
    });
  }, [checkout, order, cartDetails]);

  type HandlePriceChangeParams<T extends UIPriceChangeResolution> = T extends "ACCEPTED"
    ? [resolution: "ACCEPTED"]
    : [resolution: "DECLINED", token?: string];

  const handlePriceChange = useCallback(
    <T extends UIPriceChangeResolution>(...[resolution, token]: HandlePriceChangeParams<T>) => {
      const addOnOrderId = store.order.addOnOrder?.addOnOrderId;

      assert(priceChangeState);

      const payload: { resolution: UIPriceChangeResolution; acceptedPrice?: UIPrice } = {
        resolution
      };

      if (resolution === "ACCEPTED") {
        payload.acceptedPrice = priceChangeState.newPrice;
      }

      setPriceChangeProcessingState(true);

      let handlePriceChangeUrl = `/api/order/handlePriceChange/${orderToken}`;

      if (addOnOrderId) {
        handlePriceChangeUrl += `?addOnOrderId=${addOnOrderId}`;
      }

      clientFetch<UIPriceChangeResolutionResponse>(handlePriceChangeUrl, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "X-Flights-Context-Name": contextName
        }
      })
        .then((handlePriceChangeResponse) => {
          setPriceChangeProcessingState(false);

          if (handlePriceChangeResponse.status === "AWAITING_PAYMENT") {
            assert(handlePriceChangeResponse.otaCartReference);
            assert(handlePriceChangeResponse.otaCartToken);
            window.location.reload();
          } else if (handlePriceChangeResponse.status === "CANCELLED") {
            setPriceChangeState(undefined);
            token ? redirectToFlightDetails(token) : redirectToSearch();
          } else {
            setPriceChangeState(undefined);
            setPriceChangeError(`Unknown error when handling price change: ${handlePriceChangeResponse.status}`);
          }
        })
        .catch((error) => {
          setPriceChangeState(undefined);
          setPriceChangeProcessingState(false);
          setPriceChangeError("Something went wrong when handling the ");
          throw error;
        });
    },
    [
      store.order.addOnOrder?.addOnOrderId,
      priceChangeState,
      orderToken,
      contextName,
      redirectToFlightDetails,
      redirectToSearch
    ]
  );

  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const cartValidationErrorActions = useActions(CartValidationErrorActions);

  const { isDesktop, isMobile } = useProfiler();

  const { isPaymentForSavedAlternativeFare } = useTrackSelectedAlternativeFare();

  const onPayClick = useCallback(() => {
    const isPayNowValidationVariant = !!flights_payment_component_pay_now_validation_web.trackWithDefaultStage();
    trackClient();

    const isPCLoadingStepsExperimentEnabled = isDesktop() && !!flights_pc_loading_steps_desktop.trackWithDefaultStage();
    trackPcLoadingClientAndSupplierType(order.supplierType);

    if (isPCLoadingStepsExperimentEnabled) {
      dispatch(PaymentLoadingStatusActions.setLoadingStepsEnabled());
      loadingStepsEnabled.current = true;
      // Set in session storage also incase of agency 3DS flow where we get redirected back
      // to the confirmation page during the finalize step
      flightsSessionStore.set("PC_LOADING_STEPS_ENABLED", true);
    }

    if ((travelerHadPhoneValidationError || travelerHadEmailValidationError) && !anyValidationErrorGoalAlreadyTracked) {
      trackAnyValidationErrorAndClickPay();
      cartValidationErrorActions.setAnyValidationErrorTracked();
    }

    if (pcPaymentMethod === "PAYPAL" || pcPaymentMethod === "GOOGLE_PAY_DI") {
      const isPreventInfiniteLoaderVariant =
        !!flights_prevent_infinite_loader_gpay_and_paypal_web.trackWithDefaultStage();
      setIsPreventInfiniteLoaderVariant(isPreventInfiniteLoaderVariant);
      trackDesktopMdot();
    }

    if (!pcInputValid) {
      if (pcInputValid === false) {
        trackClickPayWithPCValidationError();
        trackPayAfterPaymentComponentError();
        setValidationErrorCount(validationErrorCount + 1);
      }

      setIsValidating(true);
      pcTriggerValidation();

      // If in payment component pay now validation experiment, prevent payment if the PC fields are invalid
      if (isPayNowValidationVariant) return;
    }

    setIsValidating(false);
    trackPCValidationAttempts(validationErrorCount);
    if (validationErrorCount > 0) {
      trackHadPCValidationError();
    }

    goal("flights_checkout_payment_paynow_click");
    flightsWebSouthwest.trackCustomGoal(); // flights_web_southwest

    /* start - flights_split_bounds_us */
    if (flight) {
      const trackingData = splitBoundsTrackingStorage.getValue(flight?.token);
      if (trackingData?.price) {
        flights_split_bounds_us.goals.negative_incremental_price_pay_now_click();
      }

      if (trackingData?.genius) {
        flights_split_bounds_us.goals.genius_offer_pay_now_click();
      }
    }
    /* end - flights_split_bounds_us */

    trackV2("click_pay_now", {
      order_id: orderId,
      payment_id: paymentId
    });

    if (isPaymentForSavedAlternativeFare(flight?.token || "")) {
      flights_alternative_fares_for_price_change_and_sold_out_errors_web.goals.attempt_pay_on_suggested_fare();
    }

    if (flightsSessionStore.get("flights_web_aa_lack_of_choice_first_page_clicked")) {
      flights_web_aa_lack_of_choice.getExp(isMobile()).goals.firstPageClickAndPay();
    }
    if (flightsSessionStore.get("flights_web_aa_lack_of_choice_top_five_clicked")) {
      flights_web_aa_lack_of_choice.getExp(isMobile()).goals.topFiveClickAndPay();
    }

    /* start - flights_web_allow_skip_extras */
    if (
      order.airOrder.brandedFareIndex &&
      (order.ancillaries?.flexibleTicket || order.ancillaries?.cancelForAnyReason)
    ) {
      flights_web_allow_skip_extras.goals.click_pay_with_flex_cfar_bf();
    }
    /* end - flights_web_allow_skip_extras */

    verifyPriceChange();
  }, [
    trackClient,
    isDesktop,
    trackPcLoadingClientAndSupplierType,
    order.supplierType,
    order.airOrder.brandedFareIndex,
    order.ancillaries?.flexibleTicket,
    order.ancillaries?.cancelForAnyReason,
    travelerHadPhoneValidationError,
    travelerHadEmailValidationError,
    anyValidationErrorGoalAlreadyTracked,
    pcPaymentMethod,
    pcInputValid,
    trackPCValidationAttempts,
    validationErrorCount,
    flightsWebSouthwest,
    flight,
    trackV2,
    orderId,
    paymentId,
    isPaymentForSavedAlternativeFare,
    verifyPriceChange,
    dispatch,
    trackAnyValidationErrorAndClickPay,
    cartValidationErrorActions,
    trackDesktopMdot,
    pcTriggerValidation,
    trackClickPayWithPCValidationError,
    trackPayAfterPaymentComponentError,
    trackHadPCValidationError,
    isMobile
  ]);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!hasRunRef.current && isValidating && pcInputValid) {
      hasRunRef.current = true;
      onPayClick();
    }
  }, [isValidating, pcInputValid, onPayClick]);

  const onRetry = useCallback(async () => {
    setRetrying(true);
    // initialize order
    const orderData = {
      /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
      // TODO: deprecated
      cartReference: otaCartReference,
      cartToken: otaCartToken
    };

    assert(flight);
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    orderData["payment3DsRedirectUrl"] = decodeURI(
      // `<ORDER_TOKEN>` is a template replaced on java layer
      createUrl(`${window.location.origin}/checkout/payment/${flight.token}/<ORDER_TOKEN>`)
    );

    const orderResponse = await clientFetch<any>("/api/order", {
      method: "post",
      credentials: "same-origin",
      body: JSON.stringify(orderData),
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName,
        ...(debugAllExtraProducts ? { "X-Flights-Debug-All-Products": "1" } : {})
      }
    }).then((r) => r);

    // process order response
    const { orderId, error, orderToken } = orderResponse;

    if (error) {
      setRetrying(false);
      throw error;
    }

    if (!orderId) {
      setRetrying(false);
      throw new Error("No order id");
    }

    offerCartOrderData.set(otaCartReference, { order: orderToken });
    location.reload();
  }, [otaCartReference, otaCartToken, contextName, debugAllExtraProducts, flight, createUrl, offerCartOrderData]);

  const totalPriceWithMP = useMemo((): UIPriceBreakdown => {
    const multiProductPrices = (mpState && mpState.multiProducts.map((item) => item.priceBreakdown)) || [];
    return pricingUtils.sumPriceBreakdown([totalPrice, ...multiProductPrices]);
  }, [mpState, totalPrice]);

  useCheckPersistedSelectedAncillaries();

  return {
    flight,
    includedProductsBySegment,
    passengers,
    totalPrice: totalPriceWithMP,
    ancillaries,
    travellerPrices: airOrder.passengerPrices,
    appliedSubsidizedFares: airOrder.appliedSubsidizedFares,
    pcContainerRef,
    onPayClick,
    pcStatus,
    onRetry,
    pcIsLoading,
    pcIsLoaded,
    pcShowError,
    isCTALoading,
    retrying,
    order,
    redirectToSearch,
    createUpdatedPriceOrder,
    handlePriceChange,
    priceChangeState,
    showPriceChangeModal,
    priceChangeError,
    isPriceChangeWithoutCharge,
    priceChangeProcessingState,
    priceChangeVerificationState,
    mpState,
    setMpState
  };
};
const CheckoutPaymentController: React.FunctionComponent<Props> = (props) => {
  const checkoutData = useCheckoutPayment();

  return props.children(checkoutData);
};

export default CheckoutPaymentController;
